/**
 * Context para compartir estado global del módulo de soporte
 *
 * Incluye soporte cross-tenant: cuando el tenant actual es "main support",
 * un dropdown global permite ver tickets de otros tenants en modo read-only.
 */

import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { ErpContext } from './ErpContext';
import SoporteHeader from '../components/soporteComponent/shared/SoporteHeader';

export const SoporteContext = createContext();

const STORAGE_KEY_TENANT_SELECCIONADO = 'soporte_tenant_seleccionado';

export const SoporteProvider = ({ children }) => {
  const { serverURL } = useContext(ErpContext);

  // Estado base del módulo
  const [categorias, setCategorias] = useState([]);
  const [prioridades, setPrioridades] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estado cross-tenant
  const [tenantsDisponibles, setTenantsDisponibles] = useState([]);
  const [isMainSupport, setIsMainSupport] = useState(false);
  const [sessionTenant, setSessionTenant] = useState(null);
  // bootstrapping = true mientras no resolvimos /tenants-disponibles.
  // Mientras está en true, useSoporteData NO debe fetchear porque podría hacerlo
  // sin el query param ?tenant=X y caer al tenant equivocado.
  const [bootstrapping, setBootstrapping] = useState(true);
  const [tenantSeleccionado, setTenantSeleccionadoState] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY_TENANT_SELECCIONADO) || null;
    } catch (_) {
      return null;
    }
  });

  const setTenantSeleccionado = useCallback((tenant) => {
    setTenantSeleccionadoState(tenant || null);
    try {
      if (tenant) sessionStorage.setItem(STORAGE_KEY_TENANT_SELECCIONADO, tenant);
      else sessionStorage.removeItem(STORAGE_KEY_TENANT_SELECCIONADO);
    } catch (_) {
      // sessionStorage puede fallar en modo privado; no es crítico
    }
  }, []);

  // Flags derivados — cross-tenant solo aplica si el tenant actual es main support
  const isAllMode = isMainSupport && tenantSeleccionado === 'all';
  // isOperandoEnOtroTenant = el usuario está actuando como soporte central sobre OTRO tenant.
  // Las acciones se ejecutan sí: la BD destino registra al usuario máscara (lutenteERP)
  // y la tabla soporte_AccionCrossTenant guarda al actor real.
  const isOperandoEnOtroTenant = isMainSupport &&
    !!tenantSeleccionado &&
    tenantSeleccionado !== sessionTenant &&
    !!sessionTenant;

  // Tenant efectivo a inyectar como query param en llamadas axios
  const tenantParaQuery = (isMainSupport &&
    tenantSeleccionado &&
    tenantSeleccionado !== 'all' &&
    tenantSeleccionado !== sessionTenant)
    ? tenantSeleccionado
    : null;

  /**
   * Cargar categorías/prioridades/config — respeta el tenant seleccionado.
   */
  const cargarDatosComunes = useCallback(async () => {
    if (!serverURL) {
      setError('Error de configuración: URL del servidor no definida');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const userRole = Cookies.get('rolUsuario') || '';
    const esAgente = userRole === 'Agente Soporte' || userRole === 'Soporte';
    const esAdmin = userRole === 'Administrador';
    const puedeVerConfig = esAgente || esAdmin;

    const params = tenantParaQuery ? { tenant: tenantParaQuery } : {};

    try {
      const promesas = [
        axios.get(`${serverURL}/soporteAPI/categorias`, { withCredentials: true, params }),
        axios.get(`${serverURL}/soporteAPI/prioridades`, { withCredentials: true, params })
      ];

      // En cross-tenant la config se trae del tenant target también (read-only)
      if (puedeVerConfig) {
        promesas.push(axios.get(`${serverURL}/soporteAPI/config`, { withCredentials: true, params }));
      }

      const resultados = await Promise.all(promesas);
      const categoriasRes = resultados[0];
      const prioridadesRes = resultados[1];
      const configRes = puedeVerConfig ? resultados[2] : null;

      const categoriasActivas = (categoriasRes.data.categorias || categoriasRes.data || [])
        .filter((c) => c.activa && !c.eliminado);
      const prioridadesActivas = (prioridadesRes.data.prioridades || prioridadesRes.data || [])
        .filter((p) => p.activa && !p.eliminado);
      const configData = configRes ? (configRes.data.config || configRes.data || null) : null;

      setCategorias(categoriasActivas);
      setPrioridades(prioridadesActivas);
      setConfig(configData);
    } catch (err) {
      console.error('[SoporteContext] Error cargando datos:', err);
      const esErrorConfig = err.response?.status === 403 && err.config?.url?.includes('/config');
      if (esErrorConfig && !puedeVerConfig) {
        setError(null);
      } else {
        setError(err.response?.data?.error || err.message || 'Error al cargar datos de soporte');
      }
      setCategorias([]);
      setPrioridades([]);
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [serverURL, tenantParaQuery]);

  /**
   * Cargar tenants disponibles. Si el caller no es main support
   * el endpoint devuelve 403 — lo ignoramos en silencio y dejamos
   * los flags en false (no se mostrará dropdown).
   */
  const cargarTenantsDisponibles = useCallback(async () => {
    if (!serverURL) {
      setBootstrapping(false);
      return;
    }
    try {
      const response = await axios.get(`${serverURL}/soporteAPI/tenants-disponibles`, {
        withCredentials: true
      });
      setTenantsDisponibles(response.data.tenants || []);
      setIsMainSupport(!!response.data.isMainSupport);
      setSessionTenant(response.data.sessionTenant || null);
    } catch (err) {
      if (err.response?.status === 403) {
        // Esperado: este tenant no es main support, no mostrar dropdown
        setTenantsDisponibles([]);
        setIsMainSupport(false);
        const fromCookie = Cookies.get('tenant') || null;
        setSessionTenant(fromCookie);
      } else {
        console.error('[SoporteContext] Error cargando tenants:', err);
        const fromCookie = Cookies.get('tenant') || null;
        setSessionTenant(fromCookie);
      }
    } finally {
      // Importante: marcar bootstrapping=false aunque haya fallado, para no
      // bloquear las llamadas para siempre.
      setBootstrapping(false);
    }
  }, [serverURL]);

  // ============================================
  // Interceptor global de axios: inyecta ?tenant=X en CUALQUIER request a /soporteAPI/
  // si hay un tenant cross-tenant activo. Esto cubre las llamadas directas
  // (axios.post/patch/delete) de los componentes admin/CrearTicket/DetalleTicket sin
  // tener que refactorizarlos uno por uno.
  // ============================================
  const tenantParaQueryRef = useRef(tenantParaQuery);
  useEffect(() => {
    tenantParaQueryRef.current = tenantParaQuery;
  }, [tenantParaQuery]);

  useEffect(() => {
    const interceptorId = axios.interceptors.request.use((config) => {
      try {
        const url = config.url || '';
        const baseURL = config.baseURL || '';
        const fullUrl = `${baseURL}${url}`;
        const apuntaASoporte = fullUrl.includes('/soporteAPI/');
        if (apuntaASoporte && tenantParaQueryRef.current) {
          const params = config.params || {};
          if (params.tenant === undefined) {
            config.params = { ...params, tenant: tenantParaQueryRef.current };
          }
        }
      } catch (e) {
        // No romper la request por un error en el interceptor
      }
      return config;
    });
    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, []);

  // Carga inicial: tenants disponibles (resuelve bootstrapping)
  useEffect(() => {
    cargarTenantsDisponibles();
  }, [cargarTenantsDisponibles]);

  // Recargar categorías/prioridades cuando cambia el tenant efectivo.
  // Esperar a que bootstrapping termine: si no, primera llamada va sin ?tenant=X
  // y trae los datos del tenant equivocado.
  useEffect(() => {
    if (bootstrapping) return;
    cargarDatosComunes();
  }, [cargarDatosComunes, bootstrapping]);

  // Si el tenant guardado en sessionStorage ya no está en la lista (registry cambió),
  // limpiar la selección.
  useEffect(() => {
    if (!tenantSeleccionado || tenantSeleccionado === 'all') return;
    if (tenantsDisponibles.length === 0) return;
    const existe = tenantsDisponibles.some((t) => t.tenant === tenantSeleccionado);
    if (!existe) {
      console.warn(`[SoporteContext] Tenant "${tenantSeleccionado}" ya no está en el registry, limpiando selección`);
      setTenantSeleccionado(null);
    }
  }, [tenantsDisponibles, tenantSeleccionado, setTenantSeleccionado]);

  const recargarCategorias = async () => {
    try {
      const params = tenantParaQuery ? { tenant: tenantParaQuery } : {};
      const response = await axios.get(`${serverURL}/soporteAPI/categorias`, {
        withCredentials: true,
        params
      });
      const categoriasActivas = (response.data.categorias || response.data || [])
        .filter((c) => c.activa && !c.eliminado);
      setCategorias(categoriasActivas);
    } catch (err) {
      console.error('Error recargando categorías:', err);
      setError(err.response?.data?.error || 'Error al recargar categorías');
    }
  };

  const recargarPrioridades = async () => {
    try {
      const params = tenantParaQuery ? { tenant: tenantParaQuery } : {};
      const response = await axios.get(`${serverURL}/soporteAPI/prioridades`, {
        withCredentials: true,
        params
      });
      const prioridadesActivas = (response.data.prioridades || response.data || [])
        .filter((p) => p.activa && !p.eliminado);
      setPrioridades(prioridadesActivas);
    } catch (err) {
      console.error('Error recargando prioridades:', err);
      setError(err.response?.data?.error || 'Error al recargar prioridades');
    }
  };

  const recargarConfig = async () => {
    try {
      const params = tenantParaQuery ? { tenant: tenantParaQuery } : {};
      const response = await axios.get(`${serverURL}/soporteAPI/config`, {
        withCredentials: true,
        params
      });
      const configData = response.data.config || response.data || null;
      setConfig(configData);
    } catch (err) {
      console.error('Error recargando configuración:', err);
      setError(err.response?.data?.error || 'Error al recargar configuración');
    }
  };

  const recargarTodo = async () => {
    await cargarDatosComunes();
  };

  const value = {
    categorias,
    prioridades,
    config,
    loading,
    error,
    recargarCategorias,
    recargarPrioridades,
    recargarConfig,
    recargarTodo,
    // Cross-tenant
    tenantsDisponibles,
    isMainSupport,
    sessionTenant,
    tenantSeleccionado,
    setTenantSeleccionado,
    isAllMode,
    isOperandoEnOtroTenant,
    tenantParaQuery,
    bootstrapping
  };

  return (
    <SoporteContext.Provider value={value}>
      <SoporteHeader />
      {children}
    </SoporteContext.Provider>
  );
};
